import { supabase } from '@/integrations/supabase/client';
import { UserReview, UserReviewInsert, UserReviewUpdate, ReviewWithUser } from '@/contracts/userReviews';

/**
 * Data facade for user reviews
 * Combines both product_reviews (seeded/vendor) and user_reviews (user-submitted)
 */

// Interface for seeded product reviews (from product_reviews table)
interface SeededReview {
  id: string;
  product_id: string;
  author: string;
  content: string;
  rating: number;
  title?: string;
  date?: string;
  verified?: boolean;
  created_at?: string;
}

// Convert seeded review to ReviewWithUser format
function seededToReviewWithUser(review: SeededReview): ReviewWithUser {
  return {
    id: review.id,
    product_id: review.product_id,
    user_id: 'seeded', // Placeholder for seeded reviews
    rating: review.rating,
    title: review.title || 'Review',
    content: review.content,
    verified_purchase: review.verified || true, // Seeded reviews are verified
    is_approved: true,
    approved_by: null,
    approved_at: null,
    created_at: review.created_at || review.date || new Date().toISOString(),
    updated_at: review.created_at || review.date || new Date().toISOString(),
    user_email: null,
    user_full_name: review.author,
    user_avatar_url: null,
  };
}

// Get all reviews for a product (both seeded + user-submitted)
export async function getProductReviews(productId: string): Promise<ReviewWithUser[]> {
  // Fetch seeded reviews from product_reviews table
  const { data: seededData, error: seededError } = await supabase
    .from('product_reviews')
    .select('*')
    .eq('product_id', productId)
    .order('created_at', { ascending: false });

  if (seededError) {
    console.error('Error fetching seeded reviews:', seededError);
  }

  // Fetch user-submitted reviews (approved only)
  const { data: userData, error: userError } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('product_id', productId)
    .eq('is_approved', true)
    .order('created_at', { ascending: false });

  if (userError) throw userError;

  // Convert seeded reviews to ReviewWithUser format
  const seededReviews: ReviewWithUser[] = (seededData || []).map(seededToReviewWithUser);

  // Fetch user profiles for user-submitted reviews using security definer function
  const userReviewsWithUsers = await Promise.all(
    (userData || []).map(async (review) => {
      // Use get_public_profile RPC to get non-sensitive profile data
      const { data: profiles } = await supabase
        .rpc('get_public_profile', { p_user_id: review.user_id });

      const profile = profiles?.[0];

      return {
        ...review,
        user_email: null, // Never expose email publicly
        user_full_name: profile?.full_name || null,
        user_avatar_url: profile?.avatar_url || null,
      };
    })
  );

  // Combine: seeded reviews first, then user-submitted
  return [...seededReviews, ...userReviewsWithUsers];
}

// Get user's own reviews (including unapproved)
export async function getUserReviews(userId: string): Promise<UserReview[]> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get single review by ID
export async function getReviewById(reviewId: string): Promise<ReviewWithUser | null> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('id', reviewId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  // Fetch user profile using security definer function
  const { data: profiles } = await supabase
    .rpc('get_public_profile', { p_user_id: data.user_id });

  const profile = profiles?.[0];

  return {
    ...data,
    user_email: null, // Never expose email publicly
    user_full_name: profile?.full_name || null,
    user_avatar_url: profile?.avatar_url || null,
  };
}

// Check if user has reviewed a product
export async function hasUserReviewedProduct(userId: string, productId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('id')
    .eq('user_id', userId)
    .eq('product_id', productId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
}

// Create a new review
export async function createReview(review: UserReviewInsert): Promise<UserReview> {
  const insertData = {
    product_id: review.product_id,
    user_id: review.user_id,
    rating: review.rating,
    title: review.title,
    content: review.content,
  };

  const { data, error } = await supabase
    .from('user_reviews')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update user's own review
export async function updateReview(reviewId: string, updates: UserReviewUpdate): Promise<UserReview> {
  const updateData: Record<string, any> = {};
  if (updates.rating !== undefined) updateData.rating = updates.rating;
  if (updates.title !== undefined) updateData.title = updates.title;
  if (updates.content !== undefined) updateData.content = updates.content;

  const { data, error } = await supabase
    .from('user_reviews')
    .update(updateData)
    .eq('id', reviewId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Delete review
export async function deleteReview(reviewId: string): Promise<void> {
  const { error } = await supabase
    .from('user_reviews')
    .delete()
    .eq('id', reviewId);

  if (error) throw error;
}

// Admin: Get all reviews (pending approval)
export async function getPendingReviews(): Promise<ReviewWithUser[]> {
  const { data, error } = await supabase
    .from('user_reviews')
    .select('*')
    .eq('is_approved', false)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Fetch user profiles separately for each review using security definer function
  const reviewsWithUsers = await Promise.all(
    (data || []).map(async (review) => {
      const { data: profiles } = await supabase
        .rpc('get_public_profile', { p_user_id: review.user_id });

      const profile = profiles?.[0];

      return {
        ...review,
        user_email: null, // Never expose email publicly
        user_full_name: profile?.full_name || null,
        user_avatar_url: profile?.avatar_url || null,
      };
    })
  );

  return reviewsWithUsers;
}

// Admin: Approve review
export async function approveReview(reviewId: string, adminId: string): Promise<UserReview> {
  const { data, error } = await supabase
    .from('user_reviews')
    .update({
      is_approved: true,
      approved_by: adminId,
      approved_at: new Date().toISOString(),
    })
    .eq('id', reviewId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Admin: Reject review (delete)
export async function rejectReview(reviewId: string): Promise<void> {
  await deleteReview(reviewId);
}

// Get review statistics for a product (combined from both tables)
export async function getProductReviewStats(productId: string): Promise<{
  averageRating: number;
  totalReviews: number;
  ratingDistribution: Record<number, number>;
}> {
  // Fetch seeded reviews
  const { data: seededData, error: seededError } = await supabase
    .from('product_reviews')
    .select('rating')
    .eq('product_id', productId);

  if (seededError) {
    console.error('Error fetching seeded reviews for stats:', seededError);
  }

  // Fetch approved user reviews
  const { data: userData, error: userError } = await supabase
    .from('user_reviews')
    .select('rating')
    .eq('product_id', productId)
    .eq('is_approved', true);

  if (userError) throw userError;

  // Combine all ratings
  const allRatings = [
    ...(seededData || []).map(r => r.rating),
    ...(userData || []).map(r => r.rating),
  ];

  const totalReviews = allRatings.length;

  if (totalReviews === 0) {
    return {
      averageRating: 0,
      totalReviews: 0,
      ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    };
  }

  const sum = allRatings.reduce((acc, r) => acc + r, 0);
  const averageRating = sum / totalReviews;

  const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  allRatings.forEach(r => {
    ratingDistribution[r as keyof typeof ratingDistribution]++;
  });

  return {
    averageRating: Math.round(averageRating * 10) / 10,
    totalReviews,
    ratingDistribution,
  };
}
